import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaGripVertical, FaTimes, FaSave } from 'react-icons/fa';
import api from '../../services/api';

interface PerguntaForm {
  texto: string;
  tipoPergunta: number;
  opcoes: string;
  ordem: number;
  obrigatoria: boolean;
}

interface PesquisaDto {
  id: string;
  titulo: string;
  descricao: string;
  tipo: number;
  status: number;
  dataCriacao: string;
  dataExpiracao: string | null;
  idEmpresaCriadora: string;
  perguntas: any[];
  totalRespostas: number;
}

interface Props {
  pesquisa: PesquisaDto | null;
  onClose: (saved: boolean) => void;
}

const tiposPergunta = [
  { value: 0, label: 'Texto livre' },
  { value: 1, label: 'Nota (1-5 estrelas)' },
  { value: 2, label: 'Multipla escolha' },
  { value: 3, label: 'Sim / Nao' },
  { value: 4, label: 'NPS (0-10)' },
];

const tiposPesquisa = [
  { value: 0, label: 'Satisfacao' },
  { value: 1, label: 'Mercado' },
  { value: 2, label: 'Feedback' },
  { value: 3, label: 'NPS' },
  { value: 4, label: 'Outro' },
];

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex; align-items: flex-start; justify-content: center;
  z-index: 1050; padding: 40px 20px;
  overflow-y: auto;
`;

const Modal = styled.div`
  background: #fff; border-radius: 16px;
  width: 100%; max-width: 720px;
  box-shadow: 0 25px 60px rgba(0,0,0,0.2);
  animation: ${slideUp} 0.25s ease-out;
`;

const ModalHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 24px 28px; border-bottom: 1px solid #f1f5f9;
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem; font-weight: 700; color: #0f172a; margin: 0;
`;

const CloseBtn = styled.button`
  border: none; background: none; cursor: pointer; color: #94a3b8;
  font-size: 1.2rem; padding: 4px;
  &:hover { color: #475569; }
`;

const ModalBody = styled.div`
  padding: 24px 28px; max-height: 65vh; overflow-y: auto;
`;

const ModalFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 28px; border-top: 1px solid #f1f5f9;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block; font-size: 0.82rem; font-weight: 600;
  color: #374151; margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%; padding: 10px 14px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  font-size: 0.9rem; color: #0f172a;
  transition: border-color 0.2s;
  &:focus { border-color: #3b82f6; outline: none; }
`;

const TextArea = styled.textarea`
  width: 100%; padding: 10px 14px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  font-size: 0.9rem; color: #0f172a; min-height: 80px; resize: vertical;
  &:focus { border-color: #3b82f6; outline: none; }
`;

const Select = styled.select`
  width: 100%; padding: 10px 14px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  font-size: 0.9rem; color: #0f172a; background: #fff;
  &:focus { border-color: #3b82f6; outline: none; }
`;

const Row = styled.div`
  display: flex; gap: 16px;
  @media (max-width: 600px) { flex-direction: column; }
`;

const Divider = styled.div`
  border-top: 2px solid #f1f5f9;
  margin: 24px 0 16px;
  position: relative;
`;

const DividerTitle = styled.span`
  background: #fff; padding: 0 12px;
  position: relative; top: -12px; left: 8px;
  font-size: 0.85rem; font-weight: 700; color: #3b82f6;
`;

const PerguntaCard = styled.div`
  background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 12px; padding: 16px; margin-bottom: 12px;
  position: relative;
`;

const PerguntaHeader = styled.div`
  display: flex; align-items: center; gap: 8px; margin-bottom: 12px;
`;

const PerguntaNum = styled.span`
  background: #3b82f6; color: #fff;
  width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.75rem; font-weight: 700; flex-shrink: 0;
`;

const RemovePerguntaBtn = styled.button`
  position: absolute; top: 12px; right: 12px;
  border: none; background: none; color: #ef4444;
  cursor: pointer; font-size: 0.85rem; padding: 4px;
  &:hover { color: #dc2626; }
`;

const AddPerguntaBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 16px; border: 2px dashed #cbd5e1;
  border-radius: 10px; background: transparent;
  color: #64748b; font-size: 0.85rem; font-weight: 500;
  cursor: pointer; width: 100%;
  justify-content: center; transition: all 0.2s;
  &:hover { border-color: #3b82f6; color: #3b82f6; background: #eff6ff; }
`;

const CheckboxLabel = styled.label`
  display: flex; align-items: center; gap: 6px;
  font-size: 0.82rem; color: #475569; cursor: pointer;
`;

const Btn = styled.button<{ primary?: boolean }>`
  padding: 10px 20px; border-radius: 10px; font-size: 0.9rem;
  font-weight: 600; cursor: pointer; transition: all 0.2s;
  display: flex; align-items: center; gap: 6px;
  border: ${p => p.primary ? 'none' : '1.5px solid #e2e8f0'};
  background: ${p => p.primary ? '#3b82f6' : '#fff'};
  color: ${p => p.primary ? '#fff' : '#475569'};
  &:hover { background: ${p => p.primary ? '#2563eb' : '#f8fafc'}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const PesquisaFormModal: React.FC<Props> = ({ pesquisa, onClose }) => {
  const isEditing = !!pesquisa;

  const [titulo, setTitulo] = useState(pesquisa?.titulo || '');
  const [descricao, setDescricao] = useState(pesquisa?.descricao || '');
  const [tipo, setTipo] = useState(pesquisa?.tipo ?? 0);
  const [dataExpiracao, setDataExpiracao] = useState(pesquisa?.dataExpiracao?.split('T')[0] || '');
  const [perguntas, setPerguntas] = useState<PerguntaForm[]>(
    pesquisa?.perguntas?.map((p: any) => ({
      texto: p.texto,
      tipoPergunta: p.tipoPergunta,
      opcoes: p.opcoes || '',
      ordem: p.ordem,
      obrigatoria: p.obrigatoria,
    })) || []
  );
  const [salvando, setSalvando] = useState(false);

  const adicionarPergunta = () => {
    setPerguntas([...perguntas, {
      texto: '',
      tipoPergunta: 0,
      opcoes: '',
      ordem: perguntas.length + 1,
      obrigatoria: true,
    }]);
  };

  const removerPergunta = (index: number) => {
    setPerguntas(perguntas.filter((_, i) => i !== index).map((p, i) => ({ ...p, ordem: i + 1 })));
  };

  const atualizarPergunta = (index: number, campo: keyof PerguntaForm, valor: any) => {
    const novas = [...perguntas];
    (novas[index] as any)[campo] = valor;
    setPerguntas(novas);
  };

  const handleSalvar = async () => {
    if (!titulo.trim()) { toast.warning('Informe o titulo da pesquisa'); return; }
    if (perguntas.length === 0) { toast.warning('Adicione pelo menos uma pergunta'); return; }
    if (perguntas.some(p => !p.texto.trim())) { toast.warning('Preencha o texto de todas as perguntas'); return; }

    setSalvando(true);
    try {
      const payload = {
        id: pesquisa?.id,
        titulo, descricao, tipo,
        // Fim do dia em hora de Brasília, sem "Z" (a API compara com o relógio de Brasília).
        dataExpiracao: dataExpiracao ? `${dataExpiracao}T23:59:59` : null,
        perguntas: perguntas.map((p, i) => ({ ...p, ordem: i + 1 })),
      };

      if (isEditing) {
        await api.put('/Pesquisas/', payload);
        toast.success('Pesquisa atualizada');
      } else {
        await api.post('/Pesquisas/', payload);
        toast.success('Pesquisa criada');
      }
      onClose(true);
    } catch {
      toast.error('Erro ao salvar pesquisa');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Overlay onClick={() => onClose(false)}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{isEditing ? 'Editar Pesquisa' : 'Nova Pesquisa'}</ModalTitle>
          <CloseBtn onClick={() => onClose(false)}><FaTimes /></CloseBtn>
        </ModalHeader>

        <ModalBody>
          <FormGroup>
            <Label>Titulo *</Label>
            <Input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ex: Pesquisa de Satisfacao Q1 2026" />
          </FormGroup>

          <FormGroup>
            <Label>Descricao</Label>
            <TextArea value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descreva o objetivo desta pesquisa..." />
          </FormGroup>

          <Row>
            <FormGroup style={{ flex: 1 }}>
              <Label>Tipo</Label>
              <Select value={tipo} onChange={e => setTipo(Number(e.target.value))}>
                {tiposPesquisa.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </FormGroup>
            <FormGroup style={{ flex: 1 }}>
              <Label>Data de expiracao (opcional)</Label>
              <Input type="date" value={dataExpiracao} onChange={e => setDataExpiracao(e.target.value)} />
            </FormGroup>
          </Row>

          <Divider><DividerTitle>Perguntas ({perguntas.length})</DividerTitle></Divider>

          {perguntas.map((p, index) => (
            <PerguntaCard key={index}>
              <RemovePerguntaBtn onClick={() => removerPergunta(index)} title="Remover pergunta">
                <FaTrash />
              </RemovePerguntaBtn>
              <PerguntaHeader>
                <PerguntaNum>{index + 1}</PerguntaNum>
                <Input
                  value={p.texto}
                  onChange={e => atualizarPergunta(index, 'texto', e.target.value)}
                  placeholder="Texto da pergunta"
                  style={{ flex: 1 }}
                />
              </PerguntaHeader>
              <Row>
                <FormGroup style={{ flex: 1 }}>
                  <Label>Tipo de resposta</Label>
                  <Select value={p.tipoPergunta} onChange={e => atualizarPergunta(index, 'tipoPergunta', Number(e.target.value))}>
                    {tiposPergunta.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Select>
                </FormGroup>
                <FormGroup style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 16 }}>
                  <CheckboxLabel>
                    <input type="checkbox" checked={p.obrigatoria} onChange={e => atualizarPergunta(index, 'obrigatoria', e.target.checked)} />
                    Obrigatoria
                  </CheckboxLabel>
                </FormGroup>
              </Row>
              {p.tipoPergunta === 2 && (
                <FormGroup>
                  <Label>Opcoes (separadas por virgula)</Label>
                  <Input
                    value={p.opcoes}
                    onChange={e => atualizarPergunta(index, 'opcoes', e.target.value)}
                    placeholder='Ex: Otimo,Bom,Regular,Ruim'
                  />
                </FormGroup>
              )}
            </PerguntaCard>
          ))}

          <AddPerguntaBtn onClick={adicionarPergunta}>
            <FaPlus /> Adicionar Pergunta
          </AddPerguntaBtn>
        </ModalBody>

        <ModalFooter>
          <Btn onClick={() => onClose(false)}>Cancelar</Btn>
          <Btn primary onClick={handleSalvar} disabled={salvando}>
            <FaSave /> {salvando ? 'Salvando...' : 'Salvar'}
          </Btn>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default PesquisaFormModal;
